<template>
    <BaseConfirm
        style="width: 780px"
        :title="'포인트 회수 요청'"
        :isOpen="isOpen"
        :confirm-text="'등록'"
        :ignore-backdrop-click="true"
        @accept="submitHandler"
        @cancel="closeHandler"
    >
        <form ref="pointsRetrivalRequestFormDOM">
            <div class="form-input-group">
                <div class="title">
                    <p class="required">회수사유</p>
                    <p class="caption">최대 50자까지 입력 가능</p>
                </div>
                <div class="contents">
                    <BaseInput
                        :name="'description'"
                        :placeholder="'예시: 2023년 복지 포인트 회수 (최대 50자)'"
                        :hasError="submitCount > 0 && !!errors.description"
                        v-model="description"
                    />
                </div>
            </div>

            <div class="form-input-group">
                <div class="title">
                    <p class="required">회수유형</p>
                </div>
                <div class="contents">
                    <BaseSelect
                        :Name="'policyId'"
                        :option-list="bizPointPolicies"
                        v-model="policyId"
                    />
                </div>
            </div>

            <div class="form-input-group">
                <div class="title">
                    <p class="required">회수대상</p>
                    <p class="caption">
                        첨부 파일명은 확장자(.xlsx, .xls) 포함<br />
                        최대 50자까지 가능
                    </p>
                </div>
                <div class="contents">
                    <div class="file-input-row">
                        <input
                            ref="fileInputDOM"
                            type="file"
                            name="excelFile"
                            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            @change="onFileChange"
                        />

                        <div
                            :class="[
                                'file-path-box',
                                {
                                    'file-path-box--empty': !isFileUploaded,
                                    'file-path-box--invalid':
                                        submitCount > 0 && !!errors.excelFile,
                                },
                            ]"
                            @click="uploadFile"
                        >
                            {{
                                excelFile ||
                                '첨부된 파일이 없습니다. (첨부 파일명은 확장자 포함 최대 50자)'
                            }}
                        </div>

                        <div class="flex justify-between">
                            <div class="error-message">
                                <p v-if="submitCount > 0 && errors.excelFile">
                                    {{ errors.excelFile }}
                                </p>
                            </div>

                            <NButton
                                class="upload-btn"
                                icon-placement="right"
                                type="primary"
                                size="large"
                                strong
                                @click="uploadFile"
                            >
                                <template #icon>
                                    <img
                                        src="@/assets/image/icn_upload.png"
                                        alt="회수대상 파일 첨부 버튼 아이콘"
                                    />
                                </template>
                                회수대상 파일 첨부
                            </NButton>
                        </div>
                    </div>

                    <div class="btn-row">
                        <NButton
                            type="success"
                            size="large"
                            strong
                            @click="downloadExcelSampleFile"
                        >
                            신청양식 파일 다운로드
                        </NButton>
                    </div>

                    <div class="notice">
                        <p>유의사항</p>
                        <ul class="list-dots">
                            <li>
                                반드시
                                <span class="font-bold"
                                    >회수 신청양식 파일을 사용하여 회수 요청을
                                    진행</span
                                >해 주셔야 합니다. 상단 초록색 「신청양식 파일
                                다운로드」 버튼을 클릭하시면, 신청양식 파일을
                                받을 수 있습니다.
                            </li>
                            <li>
                                1회 업로드 시 최대 30,000명까지 회수 요청이
                                가능합니다.
                            </li>
                            <li>
                                잘못 기입한 대상에게 포인트 회수된 경우 당사는
                                책임지지 않습니다.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </form>
    </BaseConfirm>
</template>

<script setup lang="ts">
import { getBizPointTargetList } from '@/api/points/biz-point-api';
import {fetchPointRetrieveRequest as fetchPointRetrieveRequestApi} from '@/api/points/point-request-history-api';
import BaseInput from '@/components/common/input/BaseInput.vue';
import BaseSelect from '@/components/common/input/BaseSelect.vue';
import BaseConfirm from '@/components/common/popup/BaseConfirm.vue';
import {useFieldModelCustom} from '@/hooks/vee-validate/useFieldModelCustom';
import {PointRetrieveRequestFormStateT} from '@/types/points/point-request-history-types';
import * as Sentry from '@sentry/vue';
import {AxiosError} from 'axios';
import {NButton} from 'naive-ui';
import {useForm} from 'vee-validate';
import {computed, onMounted, ref} from 'vue';
import {object, string} from 'yup';

// props, emits, libs
interface PropsI {
    isOpen: boolean;
}

withDefaults(defineProps<PropsI>(), {
    isOpen: false,
});

const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'refresh'): void;
}>();

const bizPointPolicies = ref<{text: string, value: number}[]>([]);

const {
    values: formState,
    setFieldValue,
    resetForm,
    submitCount,
    errors,
    handleSubmit,
} = useForm({
    initialValues: {
        description: '',
        excelFile: '',
        policyId: -1,
    } as PointRetrieveRequestFormStateT,
    validationSchema: object({
        description: string()
            .required('회수사유를 입력해주세요.')
            .matches(
                /* eslint-disable no-useless-escape */
                /^(?!.*[@|#|%|&|*|\/|\\|~]).*(?=[a-zA-Z0-9ㄱ-힣]).*$/,
                `특수문자 @, #, %, &, *, /, ＼, ~는 사용할 수 없습니다.`,
            )
            .max(
                50,
                (description) =>
                    `회수사유가 너무 깁니다. 회수사유는 50자 이하까지 입력 가능합니다.\n※ 현재 회수사유 길이: ${description.value.length}자`,
            ),
        excelFile: string()
            .required('xlsx, xls 형식의 회수대상 Excel 파일을 업로드해주세요.')
            .max(
                50,
                (excelFile) =>
                    `파일명이 너무 깁니다. 파일명은 확장자(.xlsx, .xls) 포함 50자 이하까지 가능합니다.\n현재 파일명 길이: ${excelFile.value.length}자`,
            ),
    }),
});

// 포인트 지급유형
const getBizPointPolicies = async () => {
    try {
        const response = await getBizPointTargetList({
            type: 'RETRIEVE',
        });
        bizPointPolicies.value = response.map(o => {
            return {
                text: o.title,
                value: o.policyId,
            }
        });

        if (bizPointPolicies.value.length > 0) {
            setFieldValue('policyId', bizPointPolicies.value[0].value);
        }
    } catch (e) {
        console.log(e);
    }
}

// Form state 참조 & 업데이트용
const {description, excelFile, policyId} = useFieldModelCustom<{
    description: string;
    excelFile: string;
    policyId: number;
}>(['description', 'excelFile', 'policyId']);

// ref, reactive
const fileInputDOM = ref<HTMLInputElement>();
const pointsRetrivalRequestFormDOM = ref<HTMLFormElement>();

// computed
const isFileUploaded = computed(() => formState.excelFile);

// methods
const closeHandler = () => {
    resetForm();
    emits('close');
};

const submitHandler = handleSubmit(async () => {
    if (!confirm(`이대로 포인트 회수 요청을 진행하시겠습니까?`)) return;

    try {
        // FIXME: postForm으로 수정
        const formData = new FormData(pointsRetrivalRequestFormDOM.value);

        await fetchPointRetrieveRequestApi(formData);

        alert('포인트 회수 요청이 완료되었습니다.');
        closeHandler();

        // NOTE: 요청 완료후 내역 리스트 0페이지로 refresh
        emits('refresh');
    } catch (err) {
        const axiosError = err as AxiosError;

        console.error(axiosError);

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                header: axiosError.config.headers,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/points/request-history/popup/PointProvideRequestPopup.vue',
            },
        });

        window.customAlert(axiosError.message);
    }
});

/**
 * @description 샘플 파일 다운로드
 */
const downloadExcelSampleFile = () => {
    location.href = `${
        import.meta.env.VITE_APP_BASE_URL
    }/files/sample_point_request.xlsx`;
};

/**
 * @description display none 처리되어있는 input click 트리거하여, 파일 업로드창 오픈
 */
const uploadFile = () => {
    fileInputDOM.value?.click();
};

/**
 * @description input file 업로드 시, formState excelFile에 파일 path 저장
 */
const onFileChange = (e: Event) => {
    if (!fileInputDOM.value!.value) return;

    const isExcelFile = ['xlsx', 'xls'].some((excelFile) => {
        return fileInputDOM.value!.value.includes(excelFile);
    });

    if (!isExcelFile) {
        alert(
            'xlsx, xls 형식의 Excel 파일만 업로드할 수 있습니다. 파일 형식을 다시 한 번 확인해 주세요.',
        );
        return;
    }

    const target = e.target as HTMLInputElement;

    // HACK: input value에 한글이 있을때 자소분리되어버림. 자소분리된 문자를 정규화하여 결합 + input에 C:\fakepath\ string 제거하여 표시
    // See: https://egg-programmer.tistory.com/293
    const fileName = target.value.normalize().replace(/C:\\fakepath\\/i, '');

    setFieldValue('excelFile', fileName);
};

onMounted(() => {
    getBizPointPolicies();
})
</script>

<style lang="scss" scoped>
.form-input-group {
    display: flex;

    & + & {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #ebebeb;
    }

    .title {
        position: relative;
        padding-top: 7px;
        width: 25%;
        font-weight: 600;
        word-break: keep-all;

        &.required,
        .required {
            &::after {
                margin-left: 1px;
                content: '*';
                color: $danger;
                line-height: 100%;
                font-size: 10px;
                vertical-align: super;
            }
        }

        .caption {
            margin-top: 3px;
            color: $gray;
            font-size: 8px;
            font-weight: 500;
            line-height: 16px;
        }
    }

    .contents {
        width: 75%;
    }

    .notice {
        margin-top: 10px;
        padding: 16px;
        border-radius: 8px;
        background-color: #f8f8fa;

        > p {
            margin-bottom: 5px;
            font-weight: bold;
        }
    }
}

.file-input-row {
    display: flex;
    flex-direction: column;

    input[type='file'] {
        display: none;

        &:focus {
            outline: 0;
        }
    }

    .file-path-box {
        width: 100%;
        height: 38px;
        padding: 5px 12px;
        border: 1px solid $black_16;
        border-radius: 6px;
        font-size: 14px;
        line-height: 1.8;
        overflow-x: auto;
        white-space: nowrap;
        cursor: pointer;

        &::-webkit-scrollbar {
            height: 3px;
        }

        &::-webkit-scrollbar-track {
            background: $black_08;
        }

        &::-webkit-scrollbar-thumb {
            &:horizontal {
                background: $black_40;
                border-radius: 6px;
            }
        }

        &::-webkit-scrollbar:vertical {
            display: none;
        }

        &--invalid {
            border-color: $danger;
        }

        &--empty {
            color: $black_32;
        }

        &:hover {
            border-color: $black_40;
        }
    }
}

.btn-row {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

:deep() {
    .n-button {
        &.upload-btn {
            margin-top: 8px;
            width: auto;

            .n-button__icon {
                margin: 0 0 0 16px !important;
                display: flex;
                justify-content: center;
                align-items: center;
                max-width: none;
                width: 40px;
                height: 100%;
                border-left: 1px solid rgba(255, 255, 255, 0.2);

                .n-icon-slot {
                    padding-left: 16px;
                    width: 100%;
                }
            }
        }
    }
}
</style>
